import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';

import {
  handoverService, HandoverBatchResponse, HandoverPreviewRow,
} from '../../../services/admissions/handoverService';
import { useDebounce } from '../../../hooks/useDebounce';
import { fmtClassName } from '../../../utils/pdf/format';

/**
 * The enrollment handover, shared by the Admissions handover page and the
 * Enrollments "Enroll from admissions" dialog: the preview of Confirmed
 * applicants, search, selection, the section each one goes into, and the run.
 *
 * A class with sections needs one picked per applicant before they can be
 * enrolled; a class with exactly one section has it picked already.
 */

export const classIdOf = (r: HandoverPreviewRow): string =>
  typeof r.class === 'object' ? r.class?._id ?? '' : r.class ?? '';

export const classNameOf = (r: HandoverPreviewRow): string =>
  typeof r.class === 'object' ? r.class?.name ?? '' : r.class ?? '';

export function useHandover({ cycleId, enabled = true }: { cycleId?: string; enabled?: boolean }) {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<HandoverPreviewRow[]>([]);
  const [summary, setSummary] = useState<{ total: number; ready: number; blocked: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sectionOf, setSectionOf] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 250);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await handoverService.preview({ cycleId: cycleId || undefined });
      setRows(res.items);
      setSummary(res.summary);
      // Fresh preview — drop stale picks; a lone section is the only choice.
      setSelected(new Set());
      setSectionOf(Object.fromEntries(
        res.items.filter(r => r.sections.length === 1).map(r => [r.applicationId, r.sections[0]._id]),
      ));
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load handover preview', { variant: 'error' });
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [cycleId, enqueueSnackbar]);

  useEffect(() => { if (enabled) load(); }, [enabled, load]);

  // The preview returns the full set, so narrowing is instant.
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.name?.toLowerCase().includes(q)
      || r.applicationNumber.toLowerCase().includes(q)
      || r.grNumber?.toLowerCase().includes(q)
      || classNameOf(r).toLowerCase().includes(q));
  }, [rows, search]);

  const readyRows = useMemo(() => visibleRows.filter(r => r.ready), [visibleRows]);
  const blockedRows = useMemo(() => visibleRows.filter(r => !r.ready), [visibleRows]);

  const allReadySelected = readyRows.length > 0 && readyRows.every(r => selected.has(r.applicationId));
  const someReadySelected = !allReadySelected && readyRows.some(r => selected.has(r.applicationId));
  const toggleAll = () => setSelected(allReadySelected ? new Set() : new Set(readyRows.map(r => r.applicationId)));
  const toggleOne = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const setSection = (applicationId: string, sectionId: string) =>
    setSectionOf(prev => ({ ...prev, [applicationId]: sectionId }));

  const selectedRows = useMemo(() => rows.filter(r => selected.has(r.applicationId)), [rows, selected]);

  /** Put every selected applicant of a class into one section. */
  const setSectionForSelected = (classId: string, sectionId: string) =>
    setSectionOf(prev => ({
      ...prev,
      ...Object.fromEntries(selectedRows.filter(r => classIdOf(r) === classId).map(r => [r.applicationId, sectionId])),
    }));

  /** Selected applicants whose class has sections but who have none picked yet. */
  const missingSection = selectedRows.filter(r => r.sections.length > 0 && !sectionOf[r.applicationId]).length;

  /** Classes among the selection that have sections — for "assign to selected". */
  const sectionedClasses = useMemo(() => {
    const byClass = new Map<string, { classId: string; className: string; sections: HandoverPreviewRow['sections']; count: number }>();
    for (const r of selectedRows) {
      if (!r.sections.length) continue;
      const id = classIdOf(r);
      const entry = byClass.get(id) ?? { classId: id, className: classNameOf(r), sections: r.sections, count: 0 };
      entry.count += 1;
      byClass.set(id, entry);
    }
    return Array.from(byClass.values());
  }, [selectedRows]);

  /** The selection, counted per class and section — for the confirm step. */
  const selectionSummary = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of selectedRows) {
      const section = r.sections.find(x => x._id === sectionOf[r.applicationId]);
      const label = `${fmtClassName(classNameOf(r)) || 'No class'}${section ? ` · Section ${section.name}` : ''}`;
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return Array.from(counts, ([label, count]) => ({ label, count }));
  }, [selectedRows, sectionOf]);

  /** Runs the batch. Resolves with the outcome and the rows that were run (the preview then reloads). */
  const run = async (): Promise<{ response: HandoverBatchResponse; ranRows: HandoverPreviewRow[] } | null> => {
    if (selected.size === 0 || missingSection > 0) return null;
    setRunning(true);
    try {
      const ids = Array.from(selected);
      const response = await handoverService.batch(
        ids,
        Object.fromEntries(ids.filter(id => sectionOf[id]).map(id => [id, sectionOf[id]])),
      );
      const { summary: s } = response;
      enqueueSnackbar(
        `Handover complete: ${s.enrolled} enrolled, ${s.skipped} skipped, ${s.failed} failed`,
        { variant: s.failed > 0 || s.skipped > 0 ? 'warning' : 'success' },
      );
      const ranRows = selectedRows;
      await load();
      return { response, ranRows };
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Handover batch failed', { variant: 'error' });
      return null;
    } finally {
      setRunning(false);
    }
  };

  return {
    rows, summary, loading, load,
    searchInput, setSearchInput, search,
    visibleRows, readyRows, blockedRows,
    selected, allReadySelected, someReadySelected, toggleAll, toggleOne,
    sectionOf, setSection, setSectionForSelected, sectionedClasses, missingSection,
    selectionSummary, running, run,
  };
}
